import { exhaustiveSwitchError, getOwnProperty, hasOwnProperty } from "@supersig/utils";
function formatPath(path) {
    if (!path.length) {
        return null;
    }
    let formattedPath = "";
    for (const item of path){
        if (typeof item === "number") {
            formattedPath += `.${item}`;
        } else if (item.startsWith("(")) {
            if (formattedPath.endsWith(")")) {
                formattedPath = `${formattedPath.slice(0, -1)}, ${item.slice(1)}`;
            } else {
                formattedPath += item;
            }
        } else {
            formattedPath += `.${item}`;
        }
    }
    if (formattedPath.startsWith(".")) {
        return formattedPath.slice(1);
    }
    return formattedPath;
}
/** @public */ export class ValidationError extends Error {
    rawMessage;
    path;
    name = "ValidationError";
    constructor(rawMessage, path = []){
        const formattedPath = formatPath(path);
        const indentedMessage = rawMessage.split("\n").map((line, i)=>i === 0 ? line : `  ${line}`).join("\n");
        super(path ? `At ${formattedPath}: ${indentedMessage}` : indentedMessage), this.rawMessage = rawMessage, this.path = path;
    }
}
function prefixError(path, fn) {
    try {
        return fn();
    } catch (err) {
        if (err instanceof ValidationError) {
            throw new ValidationError(err.rawMessage, [
                path,
                ...err.path
            ]);
        }
        throw new ValidationError(err.toString(), [
            path
        ]);
    }
}
function typeToString(value) {
    if (value === null) return "null";
    if (Array.isArray(value)) return "an array";
    const type = typeof value;
    switch(type){
        case "bigint":
        case "boolean":
        case "function":
        case "number":
        case "string":
        case "symbol":
            return `a ${type}`;
        case "object":
            return `an ${type}`;
        case "undefined":
            return "undefined";
        default:
            exhaustiveSwitchError(type);
    }
}
/** @public */ export class Validator {
    validationFn;
    constructor(validationFn){
        this.validationFn = validationFn;
    }
    /**
   * Asserts that the passed value is of the correct type and returns it. The returned value is
   * guaranteed to be referentially equal to the passed value.
   */ validate(value) {
        const validated = this.validationFn(value);
        if (process.env.NODE_ENV !== "production" && !Object.is(value, validated)) {
            throw new ValidationError("Validator functions must return the same value they were passed");
        }
        return validated;
    }
    /**
   * Returns a new validator that also accepts null or undefined. The resulting value will always be
   * null.
   */ nullable() {
        return nullable(this);
    }
    /**
   * Returns a new validator that also accepts null or undefined. The resulting value will always be
   * null.
   */ optional() {
        return optional(this);
    }
    /**
   * Refine this validation to a new type. The passed-in validation function should throw an error
   * if the value can't be converted to the new type, or return the new type otherwise.
   */ refine(otherValidationFn) {
        return new Validator((value)=>{
            return otherValidationFn(this.validate(value));
        });
    }
    check(nameOrCheckFn, checkFn) {
        if (typeof nameOrCheckFn === "string") {
            return this.refine((value)=>{
                prefixError(`(check ${nameOrCheckFn})`, ()=>checkFn(value));
                return value;
            });
        } else {
            return this.refine((value)=>{
                nameOrCheckFn(value);
                return value;
            });
        }
    }
}
/** @public */ export class ArrayOfValidator extends Validator {
    itemValidator;
    constructor(itemValidator){
        super((value)=>{
            const arr = array.validate(value);
            for(let i = 0; i < arr.length; i++){
                prefixError(i, ()=>itemValidator.validate(arr[i]));
            }
            return arr;
        }), this.itemValidator = itemValidator;
    }
    nonEmpty() {
        return this.check((value)=>{
            if (value.length === 0) {
                throw new ValidationError("Expected a non-empty array");
            }
        });
    }
    lengthGreaterThan1() {
        return this.check((value)=>{
            if (value.length <= 1) {
                throw new ValidationError("Expected an array with length greater than 1");
            }
        });
    }
}
/** @public */ export class ObjectValidator extends Validator {
    config;
    shouldAllowUnknownProperties;
    constructor(config, shouldAllowUnknownProperties = false){
        super((object)=>{
            if (typeof object !== "object" || object === null) {
                throw new ValidationError(`Expected object, got ${typeToString(object)}`);
            }
            for (const [key, validator] of Object.entries(config)){
                prefixError(key, ()=>{
                    ;
                    validator.validate(getOwnProperty(object, key));
                });
            }
            if (!shouldAllowUnknownProperties) {
                for (const key of Object.keys(object)){
                    if (!hasOwnProperty(config, key)) {
                        throw new ValidationError(`Unexpected property`, [
                            key
                        ]);
                    }
                }
            }
            return object;
        }), this.config = config, this.shouldAllowUnknownProperties = shouldAllowUnknownProperties;
    }
    allowUnknownProperties() {
        return new ObjectValidator(this.config, true);
    }
    /**
   * Extend an object validator by adding additional properties.
   *
   * @example
   *
   * ```ts
   * const animalValidator = T.object({
   * 	name: T.string,
   * })
   * const catValidator = animalValidator.extend({
   * 	meowVolume: T.number,
   * })
   * ```
   */ extend(extension) {
        return new ObjectValidator({
            ...this.config,
            ...extension
        });
    }
}
/** @public */ export class UnionValidator extends Validator {
    key;
    config;
    unknownValueValidation;
    constructor(key, config, unknownValueValidation){
        super((input)=>{
            if (typeof input !== "object" || input === null) {
                throw new ValidationError(`Expected an object, got ${typeToString(input)}`, []);
            }
            const variant = getOwnProperty(input, key);
            if (typeof variant !== "string") {
                throw new ValidationError(`Expected a string for key "${key}", got ${typeToString(variant)}`);
            }
            const matchingSchema = hasOwnProperty(config, variant) ? config[variant] : undefined;
            if (matchingSchema === undefined) {
                return this.unknownValueValidation(input, variant);
            }
            return prefixError(`(${key} = ${variant})`, ()=>matchingSchema.validate(input));
        }), this.key = key, this.config = config, this.unknownValueValidation = unknownValueValidation;
    }
    validateUnknownVariants(unknownValueValidation) {
        return new UnionValidator(this.key, this.config, unknownValueValidation);
    }
}
/** @public */ export class DictValidator extends Validator {
    keyValidator;
    valueValidator;
    constructor(keyValidator, valueValidator){
        super((object)=>{
            if (typeof object !== "object" || object === null) {
                throw new ValidationError(`Expected object, got ${typeToString(object)}`);
            }
            for (const [key, value] of Object.entries(object)){
                prefixError(key, ()=>{
                    keyValidator.validate(key);
                    valueValidator.validate(value);
                });
            }
            return object;
        }), this.keyValidator = keyValidator, this.valueValidator = valueValidator;
    }
}
function typeofValidator(type) {
    return new Validator((value)=>{
        if (typeof value !== type) {
            throw new ValidationError(`Expected ${type}, got ${typeToString(value)}`);
        }
        return value;
    });
}
/**
 * Validation that accepts any value. Useful as a starting point for building your own custom
 * validations.
 *
 * @public
 */ export const unknown = new Validator((value)=>value);
/**
 * Validation that accepts any value. Generally this should be avoided, but you can use it as an
 * escape hatch if you want to work without validations for e.g. a prototype.
 *
 * @public
 */ export const any = new Validator((value)=>value);
/**
 * Validates that a value is a string.
 *
 * @public
 */ export const string = typeofValidator("string");
/**
 * Validates that a value is a finite non-NaN number.
 *
 * @public
 */ export const number = typeofValidator("number").check((number)=>{
    if (Number.isNaN(number)) {
        throw new ValidationError("Expected a number, got NaN");
    }
    if (!Number.isFinite(number)) {
        throw new ValidationError(`Expected a finite number, got ${number}`);
    }
});
/**
 * Fails if value \< 0
 *
 * @public
 */ export const positiveNumber = number.check((value)=>{
    if (value < 0) throw new ValidationError(`Expected a positive number, got ${value}`);
});
/**
 * Fails if value \<= 0
 *
 * @public
 */ export const nonZeroNumber = number.check((value)=>{
    if (value <= 0) throw new ValidationError(`Expected a non-zero positive number, got ${value}`);
});
/**
 * Fails if number is not an integer
 *
 * @public
 */ export const integer = number.check((value)=>{
    if (!Number.isInteger(value)) throw new ValidationError(`Expected an integer, got ${value}`);
});
/**
 * Fails if value \< 0 and is not an integer
 *
 * @public
 */ export const positiveInteger = integer.check((value)=>{
    if (value < 0) throw new ValidationError(`Expected a positive integer, got ${value}`);
});
/**
 * Fails if value \<= 0 and is not an integer
 *
 * @public
 */ export const nonZeroInteger = integer.check((value)=>{
    if (value <= 0) throw new ValidationError(`Expected a non-zero positive integer, got ${value}`);
});
/**
 * Validates that a value is boolean.
 *
 * @public
 */ export const boolean = typeofValidator("boolean");
/**
 * Validates that a value is a bigint.
 *
 * @public
 */ export const bigint = typeofValidator("bigint");
/**
 * Validates that a value matches another that was passed in.
 *
 * @example
 *
 * ```ts
 * const trueValidator = T.literal(true)
 * ```
 *
 * @public
 */ export function literal(expectedValue) {
    return new Validator((actualValue)=>{
        if (actualValue !== expectedValue) {
            throw new ValidationError(`Expected ${expectedValue}, got ${JSON.stringify(actualValue)}`);
        }
        return expectedValue;
    });
}
/**
 * Validates that a value is an array. To check the contents of the array, use T.arrayOf.
 *
 * @public
 */ export const array = new Validator((value)=>{
    if (!Array.isArray(value)) {
        throw new ValidationError(`Expected an array, got ${typeToString(value)}`);
    }
    return value;
});
/**
 * Validates that a value is an array whose contents matches the passed-in validator.
 *
 * @public
 */ export function arrayOf(itemValidator) {
    return new ArrayOfValidator(itemValidator);
}
/** @public */ export const unknownObject = new Validator((value)=>{
    if (typeof value !== "object" || value === null) {
        throw new ValidationError(`Expected object, got ${typeToString(value)}`);
    }
    return value;
});
/**
 * Validate an object has a particular shape.
 *
 * @public
 */ export function object(config) {
    return new ObjectValidator(config);
}
function isValidJson(value) {
    if (value === null || typeof value === "number" || typeof value === "string" || typeof value === "boolean") {
        return true;
    }
    if (Array.isArray(value)) {
        return value.every(isValidJson);
    }
    if (typeof value === "object") {
        return Object.values(value).every(isValidJson);
    }
    return false;
}
/**
 * Validate that a value is valid JSON.
 *
 * @public
 */ export const jsonValue = new Validator((value)=>{
    if (isValidJson(value)) {
        return value;
    }
    throw new ValidationError(`Expected json serializable value, got ${typeof value}`);
});
/**
 * Validate an object has a particular shape.
 *
 * @public
 */ export function jsonDict() {
    return dict(string, jsonValue);
}
/**
 * Validation that an option is a dict with particular keys and values.
 *
 * @public
 */ export function dict(keyValidator, valueValidator) {
    return new DictValidator(keyValidator, valueValidator);
}
/**
 * Validate a union of several object types. Each object must have a property matching `key` which
 * should be a unique string.
 *
 * @example
 *
 * ```ts
 * const catValidator = T.object({ kind: T.value('cat'), meow: T.boolean })
 * const dogValidator = T.object({ kind: T.value('dog'), bark: T.boolean })
 * const animalValidator = T.union('kind', { cat: catValidator, dog: dogValidator })
 * ```
 *
 * @public
 */ export function union(key, config) {
    return new UnionValidator(key, config, (unknownValue, unknownVariant)=>{
        throw new ValidationError(`Expected one of ${Object.keys(config).map((key)=>JSON.stringify(key)).join(" or ")}, got ${JSON.stringify(unknownVariant)}`, [
            key
        ]);
    });
}
/**
 * A named object with an ID. Errors will be reported as being part of the object with the given
 * name.
 *
 * @public
 */ export function model(name, validator) {
    return new Validator((value)=>{
        const prefix = value && typeof value === "object" && "id" in value && typeof value.id === "string" ? `${name}(id = ${value.id})` : name;
        return prefixError(prefix, ()=>validator.validate(value));
    });
}
/** @public */ export function setEnum(values) {
    return new Validator((value)=>{
        if (!values.has(value)) {
            const valuesString = Array.from(values, (value)=>JSON.stringify(value)).join(" or ");
            throw new ValidationError(`Expected ${valuesString}, got ${value}`);
        }
        return value;
    });
}
/** @public */ export function optional(validator) {
    return new Validator((value)=>{
        if (value === undefined) return undefined;
        return validator.validate(value);
    });
}
/** @public */ export function nullable(validator) {
    return new Validator((value)=>{
        if (value === null) return null;
        return validator.validate(value);
    });
}
/** @public */ export function literalEnum(...values) {
    return setEnum(new Set(values));
}
